import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminGetAssignmentReview,
  adminReopenAssignmentReview,
  type ConferenceAdminReview,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";

export function AdminViewReviewPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { t, locale } = useLanguage();
  const [data, setData] = useState<ConferenceAdminReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    if (!assignmentId) return;
    setData(await adminGetAssignmentReview(assignmentId));
  };

  useEffect(() => {
    if (!assignmentId) return;
    setBusy(true);
    reload()
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, t.common.error]);

  const recLabel = (value?: string | null) => {
    switch (value) {
      case "accept":
        return t.reviews.recAccept;
      case "minor_revision":
        return t.reviews.recMinor;
      case "major_revision":
        return t.reviews.recMajor;
      case "reject":
        return t.reviews.recReject;
      default:
        return value || "—";
    }
  };

  const onReopen = async () => {
    if (!assignmentId) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await adminReopenAssignmentReview(assignmentId);
      setMessage(t.admin.reviewReopened);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) return <p>{t.common.loading}</p>;
  if (!data) {
    return <div className="dn-alert dn-alert-error">{error || t.common.error}</div>;
  }

  const a = data.assignment;
  const review = data.review;
  const back = `/admin/reviewers/${a.reviewer_id}/assign-papers`;

  return (
    <div>
      <PageHeader
        title={t.admin.viewReviewTitle}
        lead={t.admin.viewReviewLead}
        backTo={back}
        backLabel={t.admin.assignPapersTitle}
      />

      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      <div className="dn-card" style={{ marginBottom: "1rem" }}>
        <h3>{data.reviewer_name}</h3>
        <p>
          {data.reviewer_email}
          <br />
          {data.reviewer_institution}
        </p>
        <p>
          <strong>{t.admin.assignColTitle}:</strong> {a.title}
          <br />
          <strong>{t.admin.assignColAuthors}:</strong> {a.authors}
          <br />
          <strong>{t.admin.assignColStatus}:</strong>{" "}
          {a.status === "completed" ? t.reviews.statusCompleted : t.reviews.statusAssigned}
        </p>
      </div>

      <div className="dn-card">
        <h3>{t.admin.submittedReview}</h3>
        {!review || review.status !== "submitted" ? (
          <p className="dn-muted">{t.admin.noSubmittedReview}</p>
        ) : (
          <>
            <p>
              <strong>{t.reviews.recommendation}:</strong> {recLabel(review.recommendation)}
            </p>
            <p>
              <strong>{t.reviews.comments}:</strong>
              <br />
              {review.comments}
            </p>
            <p className="dn-muted">
              {t.reviews.submittedAt}:{" "}
              {review.submitted_at ? formatDateTime(review.submitted_at, locale) : "—"}
            </p>
            <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={onReopen}>
              {t.admin.reopenReview}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
